import React, { useState, useCallback } from 'react';
import '@styles/GeneralPages.css';
import '@styles/Profile.css';
import { googleLogout } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { exportIndexedDB, deleteIndexedDbTable, storeJSONData } from '@utility/indexedDB_utils.js';
import { useGoogleAuth, isTokenValid, deleteAllFilesInAppDataFolder, resetDatabase } from '@utility/google_utils.js';
import LoadingScreen from '@components/LoadingScreen';
import { BubbleInput } from '@utility/common_utils';
import { FaRecycle , FaCloudUploadAlt, FaTrashAlt } from "react-icons/fa";
import { RiLogoutBoxRLine } from "react-icons/ri";
import { BiImport, BiExport } from "react-icons/bi";
import { GrUpdate } from "react-icons/gr";
import { IoIosSettings } from "react-icons/io";
import { NavLink, useLocation } from 'react-router-dom';
import { importMergeData } from '@utility/import_utils';

const Profile = () => { 
  // ===================== STATE MANAGEMENT =====================
  const navigate = useNavigate();
  const { login } = useGoogleAuth();
  
  const [state, setState] = useState({
    isUploading: false,
    isProcessing: false,
    currentOperation: null,
    confirmationCode: '',
    generatedCode: '',
    showCodeInput: false,
    username: localStorage.getItem('username') || '',
    errors: {
      uploadError: null,
      confirmationError: null
    },
    confirmationDialog: {
      show: false,
      action: null,
      title: '',
      message: '',
      data: null
    },
    exportSuccess: false,
  });

  const [ManageData, setManageData] = useState(false);
  const [ImportData, setImportData] = useState(false);
  const [importFile, setImportFile] = useState(null);


  // ===================== UTILITY FUNCTIONS =====================
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const generateConfirmationCode = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setState(prev => ({ ...prev, generatedCode: result }));
    return result;
  }, []);

  const setError = (field, message) => {
    setState(prev => ({
      ...prev,
      errors: { ...prev.errors, [field]: message }
    }));
  };

  // ===================== CORE OPERATIONS =====================
  const logoutCompletely = async () => {
    // Clear localStorage more thoroughly
    const itemsToKeep = []; // Add any items you want to preserve
    Object.keys(localStorage).forEach(key => {
      if (!itemsToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Clear IndexedDB more reliably
    await new Promise((resolve) => {  
      const req = indexedDB.deleteDatabase('workoutDB');
      req.onsuccess = resolve;
      req.onerror = resolve;
      req.onblocked = resolve;
    });

    googleLogout();
    navigate('/');
  };

  const logoutGuestCompletely = () => {
    ['isGuest', 'username', 'profilePicture', 'isLoggingWorkout', 
     'currentWorkout', 'isLoggingWorkout']
      .forEach(item => localStorage.removeItem(item));
    
    deleteIndexedDbTable();
    navigate('/');
  };

  const reauthenticate = useCallback(async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    await login();
  }, [login]);

  const handleDataOperation = useCallback(async (operation) => {
    if (state.isProcessing) return;
    
    setState(prev => ({ ...prev, isProcessing: true, errors: { ...prev.errors, uploadError: null } }));
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken || !(await isTokenValid(accessToken))) {
        await reauthenticate();
        return;
      }
      
      await operation();
    } catch (error) {
      console.error("Operation failed:", error);
      
      if (error.message === 'AUTH_REQUIRED') {
        try {
          await reauthenticate();
        } catch (reauthError) {
          setError('uploadError', '🔐 Authentication failed. Please log in again.');
        }
      } else {
        setError('uploadError', `❌ Operation failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  }, [reauthenticate, state.isProcessing]);

  // ===================== ACTION HANDLERS =====================
  const showConfirmation = (action, title, message, data = null) => {
    if (data?.isGuest) {
      setState(prev => ({
        ...prev,
        confirmationDialog: {
          show: true,
          action,
          title: `⚠️ ${title}`,
          message: (
            <div className="confirmation-message">
              <div className="confirmation-text">{message}</div>
              <div className="warning-message">
                <strong>Guest data will be permanently deleted!</strong>
                <div>Export your data first if you want to save it.</div>
                <button 
                  onClick={async () => {
                    setManageData(false);
                    await handleExportData();
                    setState(prev => ({
                      ...prev,
                      confirmationDialog: {
                        ...prev.confirmationDialog,
                        show: false
                      }
                    }));
                  }}
                  disabled={state.isProcessing} 
                  className="profile-button export-data-button"
                >
                  {state.isProcessing ? '⏳ Processing...' : (<><BiExport/>Export Data</>)}
                </button>
                {state.exportSuccess && (
                  <div className="success-message" style={{ marginTop: '10px' }}>
                    ✅ Data exported successfully!
                  </div>
                )}
              </div>
            </div>
          ),
          onConfirm: data.onConfirm,  // Pass through the callbacks
          onCancel: data.onCancel
        },
        showCodeInput: false
      }));
      return;
    }
    else if (action === 'resetDatabase' || action === 'deleteAllData') {
      const code = generateConfirmationCode();
      setState(prev => ({
        ...prev,
        showCodeInput: true,
        confirmationDialog: {
          show: true,
          action,
          title: `⚠️ ${title}`,
          message: (
            <div className="confirmation-message">
              <div>{message}</div>
              <div className="half_gap" />
              <div className="warning-message">This action cannot be undone.</div>
              <div className="half_gap" />
              <div className="given_code">{code}</div>
              <div className="half_gap" />
              <div>Enter this code to proceed:</div>
            </div>
          ),
          data
        }
      }));
      } else {
        setState(prev => ({
          ...prev,
          showCodeInput: false,
          confirmationDialog: {
            show: true,
            action,
            title: `⚠️ ${title}`,
            message,
            data
          }
        }));
      }
    };

  const handleGuestExport = async () => {
    setState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      // Replace window.confirm with our confirmation dialog
      const exportConfirmed = await new Promise((resolve) => {
        setState(prev => ({
          ...prev,
          confirmationDialog: {
            show: true,
            action: 'guestExport',
            title: 'Export Guest Data?',
            message: (
              <div className="confirmation-message">
                <p>Export your guest data before logging out?</p>
                <div className="warning-message">
                  Your data will be permanently deleted if you don't export it!
                </div>
              </div>
            ),
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          }
        }));
      });

      if (exportConfirmed) {
        await exportIndexedDB({ localExport: true });
        setError('uploadError', '✅ Data exported successfully!');
      }
      
      logoutGuestCompletely();
    } catch (error) {
      console.error("Export failed:", error);
      setError('uploadError', '❌ Failed to export data: ' + error.message);
      
      // Show error confirmation dialog
      const tryAgain = await new Promise((resolve) => {
        setState(prev => ({
          ...prev,
          confirmationDialog: {
            show: true,
            action: 'exportRetry',
            title: 'Export Failed',
            message: (
              <div className="confirmation-message">
                <p>Error: {error.message}</p>
                <p>Would you like to try exporting again?</p>
              </div>
            ),
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          }
        }));
      });
      
      if (tryAgain) {
        await handleGuestExport();
        return;
      }
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleConfirmAction = async () => {
    if (!state.confirmationDialog.action) return;
    
    // Handle confirmation code checks first
    if ((state.confirmationDialog.action === 'resetDatabase' || 
        state.confirmationDialog.action === 'deleteAllData') && 
        state.confirmationCode !== state.generatedCode) {
      setError('confirmationError', '❌ Invalid confirmation code. Please try again.');
      return;
    }

    // Check if we have direct callbacks to execute
    if (state.confirmationDialog.onConfirm) {
      try {
        await state.confirmationDialog.onConfirm();
      } catch (error) {
        console.error("Confirmation action failed:", error);
        setError('uploadError', `❌ Action failed: ${error.message}`);
      }
      setState(prev => ({
        ...prev,
        confirmationDialog: { ...prev.confirmationDialog, show: false }
      }));
      return;
    }

    // Handle operations that need processing state
    setState(prev => ({ 
      ...prev, 
      currentOperation: state.confirmationDialog.action,
      isProcessing: true,
      errors: { ...prev.errors, confirmationError: null }
    }));
    
    try {
      switch (state.confirmationDialog.action) {
        case 'resetDatabase':
          await resetDatabase();
          setError('uploadError', '✅ Database reset successfully!');
          break;
        case 'deleteAllData':
          await handleDataOperation(() => deleteAllFilesInAppDataFolder(localStorage.getItem('accessToken')));
          setError('uploadError', '✅ All data deleted successfully...');
          break;
        default:
          break;
      }
    } catch (error) {
      const errorMsg = error.message || 'Operation failed';
      setError('uploadError', `❌ ${errorMsg}`);
      
      if (error.message.includes('auth') || error.message.includes('token')) {
        setError('uploadError', '🔐 Session expired. Please log in again.');
        await reauthenticate();
      }
    } finally {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        currentOperation: null,
        confirmationDialog: { ...prev.confirmationDialog, show: false }
      }));
    }
  };

  const handleLogout = async () => {
    const isGuest = localStorage.getItem('isGuest') === 'true';
    
    if (isGuest) {
      // Guest logout flow remains the same
      showConfirmation(
        'logout', 
        'Confirm Guest Logout', 
        'Are you sure you want to sign out? Your guest data will be lost unless you export it first.',
        { 
          isGuest: true,
          onConfirm: logoutGuestCompletely,
          onCancel: () => {}
        }
      );
      return;
    }

    // Authenticated user logout flow
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Check token validity
      if (!accessToken || !(await isTokenValid(accessToken))) {
        await reauthenticate();
        return;
      }

      // Show enhanced confirmation with options
      showConfirmation(
        'logout',
        'Confirm Logout',
        (
          <div className="guest-options-modal">
            <div className="modal-header-s">
              <h3>Sync Before Logout?</h3>
              <button 
                onClick={() => setState(prev => ({
                  ...prev,
                  confirmationDialog: { ...prev.confirmationDialog, show: false }
                }))} 
                className="close-btn"
              >
                &times;
              </button>
            </div>

            <p>Would you like to sync your data to Google Drive before logging out?</p>

            <div className='button-box'>
              <button 
                onClick={async () => {
                  setState(prev => ({ ...prev, isUploading: true }));
                  try {
                    await exportIndexedDB({ uploadToGoogleDrive: true });
                    logoutCompletely();
                  } catch (error) {
                    console.error("Sync failed:", error);
                    setError('uploadError', `❌ Failed to sync data: ${error.message}`);
                  } finally {
                    setState(prev => ({ ...prev, isUploading: false }));
                  }
                }} 
                className="edit-button"
              >
                <FaCloudUploadAlt /> Sync & Logout
              </button>

              <button 
                onClick={logoutCompletely}
                className="cancel-button"
              >
                <RiLogoutBoxRLine /> Logout Without Syncing
              </button>
            </div>
          </div>
        ),
        { 
          customDialog: true,
          customHeader: true // New flag to indicate custom header
        }
      );

    } catch (error) {
      console.error("Logout failed:", error);
      setError('uploadError', `❌ Logout failed: ${error.message}`);
    }
  };

  const handleUpdateData = () => {
    setState(prev => ({ ...prev, currentOperation: 'updateData' }));
    handleDataOperation(() => exportIndexedDB({ uploadToGoogleDrive: true }))
      .then(() => setError('uploadError', '✅ Data updated successfully!'))
      .catch(() => setError('uploadError', '❌ Failed to update data'))
      .finally(() => setState(prev => ({ ...prev, currentOperation: null })));
  };

  const handleDeleteAllData = () => {
    showConfirmation(
      'deleteAllData',
      'Delete All Data',
      'This will PERMANENTLY DELETE ALL your data.',
    );
  };

  const handleResetDatabase = () => {
    showConfirmation(
      'resetDatabase',
      'Reset Database',
      'This will COMPLETELY RESET all your data.',
    );
  };


  const readAndParseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          resolve(jsonData);
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
};

  const importOverrideData = async () => {
    if (!importFile) {
      setError('uploadError', '❌ Please select a file to import');
      return;
    }

    setState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      // First delete all existing data
      await deleteIndexedDbTable();
      
      // Read and parse the JSON file
      const jsonData = await readAndParseFile(importFile);
      
      // Verify the data structure
      if (!jsonData || !jsonData.tables) {
        throw new Error('Invalid data format - missing tables array');
      }
      
      // Store the new data (this will recreate the database)
      await storeJSONData(jsonData);
      
      setError('uploadError', '✅ Data overwritten successfully!');
      setImportData(false);
      setManageData(false);
    } catch (error) {
      console.error("Import failed:", error);
      setError('uploadError', `❌ Import failed: ${error.message}`);
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleExportData = async () => {
    setState(prev => ({ ...prev, isProcessing: true, exportSuccess: false }));
    
    try {
      await exportIndexedDB({ localExport: true });
      setState(prev => ({ 
        ...prev, 
        exportSuccess: true,
        errors: { ...prev.errors, uploadError: '✅ Data exported successfully!' }
      }));
      handleLogout();
    } catch (error) {
      setError('uploadError', `❌ Export failed: ${error.message}`);
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleMergeData = async () => {
    if (!importFile) {
      setError('uploadError', '❌ Please select a file to import');
      return;
    }

    setState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      const jsonData = await readAndParseFile(importFile);
      
      if (!jsonData || !jsonData.tables) {
        throw new Error('Invalid data format - missing tables array');
      }
      
      // Add error handling for the merge operation
      await importMergeData(jsonData)
        .then(() => {
          setError('uploadError', '✅ Data merged successfully!');
          setImportData(false);
          setManageData(false);
        })
        .catch(error => {
          throw error;
        });
      
    } catch (error) {
      console.error("Merge failed:", error);
      setError('uploadError', `❌ Merge failed: ${error.message}`);
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // ===================== RENDER HELPERS =====================
  const getLoadingMessage = () => {
    const messages = {
      'logout': (<><RiLogoutBoxRLine /> Logging out and syncing data...</>),
      'resetDatabase': (<><FaRecycle /> Resetting your database...</>),
      'deleteAllData': (<><FaTrashAlt /> Deleting all your data...</>),
      'updateData': (<><FaCloudUploadAlt /> Updating your data...</>),
    };
    return messages[state.currentOperation] || "⏳ Processing your request...";
  };

  // ===================== COMPONENT RENDER =====================
  return (
    <div className="page-content profile-container">
      <h1 className="profile-header">👤 {state.username}'s Profile</h1>
      
      <div className="profile-actions">

        <button 
          onClick={handleUpdateData}
          disabled={state.isProcessing}
          className="profile-button"
        >
          {state.isProcessing ? '⏳ Processing...' : (<><GrUpdate/>Update Data</>)}
        </button>


        <button 
          onClick={handleLogout}
          disabled={state.isUploading || state.isProcessing}
          className="profile-button"
        >
          {state.isUploading ? (
            <>
              <span className="spinner"></span> Uploading...
            </>
          ) : (
            <>
              <RiLogoutBoxRLine/>Logout
            </>
          )}
        </button>

        <button
          onClick={() => navigate("/home/settings")}
          className="profile-button"
        >
          <IoIosSettings /> Settings
        </button>

      </div>
      <div className="bottom">
        <button onClick={() => setManageData(true)} className="profile-button">
          Manage Data
        </button>
      </div>

      {ImportData && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-dialog">
            <h3 className="dialog-title">Import Data</h3>
            <p>Import data using OVERWRITE if you wish to replace your old database, import using MERGE if you wish to merge data into your existing database.</p>
            
            {/* Add file input */}
            <div className="file-input-container">
              <input 
                type="file" 
                accept=".json" 
                onChange={handleFileChange}
                disabled={state.isProcessing}
              />
            </div>
            
            <div className="button-box">
              <button 
                className="remove-button" 
                onClick={importOverrideData}
                disabled={state.isProcessing || !importFile}
              >
                Overwrite
              </button>
              <button 
                className="edit-button"
                onClick={handleMergeData}
                disabled={state.isProcessing || !importFile}
              >
                Merge
              </button>
            </div>
            <div className="gap"></div>
            <div className="button-box">
              <button 
                className="remove-button" 
                onClick={() => { 
                  setManageData(true); 
                  setImportData(false);
                  setImportFile(null); // Reset file selection
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {ManageData && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-dialog">
            <h3 className="dialog-title">Manage Data</h3>
            <div className="button-box">
              <button onClick={() => {setManageData(false); handleExportData(); }} disabled={state.isProcessing} className="profile-button export-data-button">
                {state.isProcessing ? '⏳ Processing...' : (<><BiExport/>Export Data</>)}
              </button>
              <button onClick={() => { setManageData(false); setImportData(true);}} disabled={state.isProcessing} className="profile-button import-data-button">
                {state.isProcessing ? '⏳ Processing...' : (<><BiImport/>Import Data</>)}
              </button>
            </div>
            <div className="gap"></div>

            <button onClick={() => { setManageData(false); handleDeleteAllData();}} disabled={state.isProcessing} className="profile-button delete-data-button">
              {state.isProcessing ? '⏳ Processing...' : (<><FaTrashAlt/>Delete All Data</>)}
            </button>

            <div className="half_gap"></div>
            
            <button onClick={() => { setManageData(false); handleResetDatabase();}} disabled={state.isProcessing} className="profile-button reset-data-button">
              {state.isProcessing ? '⏳ Processing...' : (<><FaRecycle/>Reset Database</>)}
            </button>

            <div className="gap"></div>
            <button onClick={() => { setManageData(false);}} className="profile-button red-button">
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {state.errors.uploadError && (
        <div className={`error-message ${state.errors.uploadError.includes('✅') ? 'success' : 'error'}`}>
          {state.errors.uploadError}
        </div>
      )}
      
      {state.confirmationDialog.show && (
        <div className="modal-overlay">
          <div className="modal-wrapper">
            <div className={`modal-content ${state.confirmationDialog.data?.customDialog ? 'guest-options-modal' : ''}`}>
              {/* Only show standard header if not a custom dialog or if customHeader is not set */}
              {!state.confirmationDialog.data?.customHeader && (
                <h3 className="dialog-title">{state.confirmationDialog.title}</h3>
              )}
              
              {state.errors.confirmationError && (
                <div className="error-message">{state.errors.confirmationError}</div>
              )}
              
              <div className="dialog-message">
                {state.confirmationDialog.message}
              </div>
              
              {state.showCodeInput && (
                <div className="confirmation-code-input">
                  <BubbleInput
                    length={8}
                    value={state.confirmationCode}
                    onChange={(code) => setState(prev => ({ ...prev, confirmationCode: code }))}
                  />
                </div>
              )}
              
              {/* Only show standard buttons if not a custom dialog */}
              {!state.confirmationDialog.data?.customDialog && (
                <div className="modal-actions">
                  <button 
                    className="dialog-button cancel-button"
                    onClick={() => {
                      state.confirmationDialog.onCancel?.();
                      setState(prev => ({
                        ...prev,
                        confirmationDialog: { ...prev.confirmationDialog, show: false },
                        confirmationCode: '',
                        generatedCode: ''
                      }));
                    }}
                    disabled={state.isProcessing}
                  >
                    Cancel
                  </button>
                  <button 
                    className={`dialog-button ${state.confirmationDialog.action === 'deleteAllData' || 
                      state.confirmationDialog.action === 'resetDatabase' ? 'confirm-danger-button' : 'confirm-button'}`}
                    onClick={handleConfirmAction}
                    disabled={state.isProcessing || 
                      (state.showCodeInput && state.confirmationCode !== state.generatedCode)}
                  >
                    {state.isProcessing ? '⏳ Processing...' : 'Confirm'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(state.isProcessing || state.isUploading) && (
        <LoadingScreen 
          text={getLoadingMessage()}
          opacity={0.9}
        />
      )}
    </div>
  );
};

export default Profile;